import React, { useState, useEffect, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import ReactECharts from 'echarts-for-react'
import { Select, Tooltip, Typography, DatePicker, Tabs, Segmented } from 'antd'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import { useAuth } from '../hooks/AuthContext'
import { usePermission } from '../hooks/usePermission'
import { API_BASE_URL } from '../utils/apiutils'
import api from '../utils/api'
import { Result, Button, Spin } from 'antd'
import { useNavigate } from 'react-router-dom'
import {
  DollarOutlined,
  CalendarOutlined,
  UserOutlined,
  BarChartOutlined,
  ShoppingOutlined,
  CloseCircleOutlined,
  StarOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  WalletOutlined,
  BankOutlined,
  CreditCardOutlined,
  SwapOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons'
import { now as tzNow } from '../utils/timezone'

dayjs.extend(isoWeek)
dayjs.extend(quarterOfYear)

const { Text } = Typography

// Premium color palette - Swiss spa aesthetic
const colors = {
  primary: '#2C3E50', // Deep charcoal
  secondary: '#5D7A9B', // Muted blue-gray
  accent: '#8B9DC3', // Soft blue
  text: {
    primary: '#1A1A1A',
    secondary: '#6B7280',
    tertiary: '#9CA3AF'
  },
  background: {
    primary: '#FFFFFF',
    secondary: '#FAFBFC',
    tertiary: '#F5F7FA'
  },
  border: '#E5E7EB',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444'
}

// Spacing scale - perfect balance
const spacing = {
  xs: '8px',
  sm: '16px',
  md: '24px',
  lg: '32px',
  xl: '48px',
  xxl: '64px'
}

const PageContainer = styled.div`
  width: 100%;
  max-width: min(1600px, 100%);
  margin: 0 auto;
  padding: ${spacing.lg} ${spacing.md};
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 1439px) {
    max-width: 100%;
  }

  @media (max-width: 820px) {
    padding: ${spacing.md} ${spacing.sm};
    width: 100%;
    max-width: 100%;
  }
`

const DashboardHeader = styled.div`
  margin-bottom: ${spacing.xl};

  @media (max-width: 820px) {
    margin-bottom: ${spacing.lg};
  }
`

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 300;
  color: ${colors.text.primary};
  margin: 0 0 ${spacing.md} 0;
  letter-spacing: -0.5px;

  @media (max-width: 820px) {
    font-size: 24px;
    margin-bottom: ${spacing.sm};
  }
`

const FiltersContainer = styled.div`
  display: flex;
  gap: ${spacing.md};
  align-items: center;
  padding: ${spacing.md};
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 820px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${spacing.sm};
    padding: ${spacing.sm};
    width: 100%;
  }
`

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};

  @media (max-width: 820px) {
    width: 100%;
    justify-content: space-between;
  }
`

const FilterLabel = styled(Text)`
  font-size: 14px;
  color: ${colors.text.secondary};
  font-weight: 500;
  white-space: nowrap;
`

const StyledSelect = styled(Select)`
  min-width: 140px;

  .ant-select-selector {
    border: 1px solid ${colors.border} !important;
    border-radius: 6px !important;
    height: 40px !important;

    &:hover {
      border-color: ${colors.secondary} !important;
    }
  }

  .ant-select-selection-item {
    line-height: 38px !important;
    color: ${colors.text.primary};
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${colors.secondary} !important;
    box-shadow: 0 0 0 2px rgba(93, 122, 155, 0.1) !important;
  }

  @media (max-width: 820px) {
    flex: 1;
    min-width: 0;
  }
`

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing.lg};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 820px) {
    gap: ${spacing.md};
    width: 100%;
  }
`

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;

  @media (max-width: 820px) {
    gap: ${spacing.md};
    width: 100%;
  }
`

const CashFlowSection = styled.section`
  margin-bottom: ${spacing.xl};
  padding: ${spacing.lg};
  background: linear-gradient(135deg, ${colors.background.secondary} 0%, ${colors.background.primary} 100%);
  border: 1px solid ${colors.border};
  border-radius: 12px;

  .section-label {
    font-size: 12px;
    font-weight: 600;
    color: ${colors.secondary};
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: ${spacing.md};
    display: flex;
    align-items: center;
    gap: ${spacing.sm};
  }
`

const CashFlowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: ${spacing.md};

  @media (max-width: 820px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${spacing.sm};
  }
`

const CashFlowCard = styled.div`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: ${spacing.md};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  .cf-label {
    font-size: 12px;
    color: ${colors.text.secondary};
    margin-bottom: ${spacing.xs};
  }
  .cf-value {
    font-size: 20px;
    font-weight: 600;
    color: ${colors.text.primary};
  }
  .cf-value.positive { color: ${colors.success}; }
  .cf-value.negative { color: ${colors.error}; }
  .cf-sub {
    font-size: 11px;
    color: ${colors.text.tertiary};
    margin-top: ${spacing.xs};
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  @media (max-width: 1439px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }

  @media (max-width: 820px) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${spacing.sm};
    width: 100%;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const StatCard = styled.div`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: ${spacing.lg};
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  @media (max-width: 820px) {
    padding: ${spacing.md};
    width: 100%;
  }
`

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};
`

const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.background.tertiary};
  color: ${colors.secondary};
  font-size: 18px;

  @media (max-width: 820px) {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }
`

const StatTitle = styled(Text)`
  font-size: 14px;
  color: ${colors.text.secondary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 300;
  color: ${colors.text.primary};
  line-height: 1.2;
  margin-top: ${spacing.xs};

  @media (max-width: 820px) {
    font-size: 24px;
  }
`

const StatSubtext = styled(Text)`
  font-size: 13px;
  color: ${colors.text.tertiary};
  margin-top: ${spacing.xs};
  display: block;
`

const ChartCard = styled.div`
  background: ${colors.background.primary};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: ${spacing.lg};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media (max-width: 820px) {
    padding: ${spacing.md};
  }
`

const ChartHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing.lg};
  padding-bottom: ${spacing.md};
  border-bottom: 1px solid ${colors.border};

  @media (max-width: 820px) {
    margin-bottom: ${spacing.md};
    padding-bottom: ${spacing.sm};
    flex-direction: column;
    align-items: flex-start;
    gap: ${spacing.xs};
  }
`

const ChartTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
`

const ChartTitleText = styled(Text)`
  font-size: 16px;
  font-weight: 500;
  color: ${colors.text.primary};
`

const ChartSubtext = styled(Text)`
  font-size: 12px;
  color: ${colors.text.tertiary};
  font-weight: 400;
`

const ChartContainer = styled.div`
  height: 320px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;

  @media (max-width: 820px) {
    height: 280px;
  }
`

const SummarySentence = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${colors.text.secondary};
  margin: 0 0 ${spacing.lg} 0;
  padding: ${spacing.md};
  background: ${colors.background.secondary};
  border-radius: 8px;
  border-left: 4px solid ${colors.secondary};
`

const RankingScrollWrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  padding-bottom: 8px;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${colors.background.secondary};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.border};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.text.tertiary};
  }
`

const RankingList = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${spacing.md};
  min-width: min-content;
  padding: ${spacing.sm} 0;
`

const RankingItem = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
  padding: ${spacing.md} ${spacing.lg};
  background: ${colors.background.secondary};
  border-radius: 8px;
  border: 1px solid ${colors.border};

  .rank {
    font-size: 18px;
    font-weight: 600;
    color: ${colors.secondary};
    margin-bottom: ${spacing.xs};
  }
  .name {
    font-size: 13px;
    color: ${colors.text.primary};
    text-align: center;
    margin-bottom: ${spacing.sm};
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .amount {
    font-size: 15px;
    font-weight: 600;
    color: ${colors.primary};
  }
  .count {
    font-size: 12px;
    color: ${colors.text.tertiary};
  }
`

const SummaryTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;

  th,
  td {
    padding: ${spacing.sm} ${spacing.md};
    text-align: left;
    border-bottom: 1px solid ${colors.border};
  }

  th {
    font-weight: 500;
    color: ${colors.text.secondary};
  }

  td:last-child {
    text-align: right;
    font-weight: 500;
    color: ${colors.text.primary};
  }

  tr:last-child td {
    border-bottom: none;
  }

  .amount-negative {
    color: ${colors.error};
  }
`

const RestrictedAccess = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  text-align: center;
  padding: ${spacing.xxl};
  background: ${colors.background.secondary};
  border-radius: 16px;
  border: 1px solid ${colors.border};
`

const RestrictedIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${colors.background.tertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing.lg};
  color: ${colors.secondary};
  font-size: 36px;
`

const RestrictedTitle = styled.h2`
  font-size: 24px;
  font-weight: 300;
  color: ${colors.text.primary};
  margin: 0 0 ${spacing.md} 0;
`

const RestrictedDescription = styled(Text)`
  font-size: 15px;
  color: ${colors.text.secondary};
  line-height: 1.6;
  max-width: 500px;
  margin-bottom: ${spacing.xl};
`

const UpgradeButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  font-size: 14px;
  font-weight: 500;
  color: ${colors.background.primary};
  background: ${colors.primary};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.3px;

  &:hover {
    background: ${colors.secondary};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(44, 62, 80, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`

const DashboardPage = () => {
  const [revenueData, setRevenueData] = useState({
    scope: 'all',
    checkedOut: { data: [], totalRevenue: 0, totalCount: 0, averageAmount: 0 },
    all: { data: [], totalRevenue: 0, totalCount: 0, averageAmount: 0 },
    totalValueAdded: 0,
    summary: {
      totalRevenueAll: 0,
      totalRevenueCheckedOut: 0,
      storedValueReceived: 0,
      storedValueUsed: 0,
      storedValueUsedFromCheckout: null,
      storedValueUsedExcludingProduct: undefined,
      totalRevenueWithStoredValue: 0,
      technologyRevenue: 0,
      productSalesRevenue: 0,
      classTicketRevenue: 0,
      servicesAndProductsSubtotal: 0,
      discountOrOtherAmount: 0
    }
  })
  // ⚠️ 報表區間的「現在」一律用店家時區。用 dayjs() 的話，店家人在 UTC+7
  // 的月初／月底深夜開報表，本月會整個查成上個月。
  const [timeRange, setTimeRange] = useState('monthly')
  const [scope, setScope] = useState('checkedOut') // 查詢範圍：優先僅已結帳；all＝已結帳＋未結帳
  const appointmentStatus = 'all' // 保留相容
  const [startDate, setStartDate] = useState(() => tzNow().startOf('month'))
  const [endDate, setEndDate] = useState(() => tzNow().endOf('month'))
  /** 使用者選擇的基準月份／年份（用於「本月」「季度」「年」的查詢區間） */
  const [selectedBaseDate, setSelectedBaseDate] = useState(() => tzNow())
  const { businessId, user } = useAuth()
  const { can } = usePermission()
  const navigate = useNavigate()
  const plan = user.plan

  const canViewFinance = can('finance:view')
  // 數據報表頁專屬權限：與「結帳/儲值記錄查看」分開
  const canViewReport = can('report:view')

  const [productSalesData, setProductSalesData] = useState({
    productSalesReport: [],
    totalSales: 0,
    totalOrders: 0
  })

  const [customerSourceData, setCustomerSourceData] = useState({
    totalBookings: 0,
    newCustomers: { count: 0, ratio: 0, identifiers: [] },
    returningCustomers: { count: 0, ratio: 0, identifiers: [] },
    customerSources: {},
    genderDistribution: {},
    ageDistribution: {}
  })

  const [cancellationData, setCancellationData] = useState({
    totalBookings: 0,
    cancelledBookings: 0,
    cancellationRate: 0,
    cancelledCustomersCount: 0,
    cancelledCustomers: []
  })

  const [reviewData, setReviewData] = useState({
    totalReviews: 0,
    positiveReviews: 0,
    negativeReviews: 0,
    positiveRate: 0,
    negativeRate: 0
  })

  const [loading, setLoading] = useState(true)

  // 分頁：overview＝總覽；customers＝顧客洞察
  const [dashTab, setDashTab] = useState('overview')

  // 預約項目熱門度排序依據：sales＝消費金額；count＝預約次數
  const [productSortBy, setProductSortBy] = useState('sales')

  // 顧客總量（全店累計，不受時間篩選影響）
  const [totalCustomers, setTotalCustomers] = useState(null)

  const [employeeProductivityData, setEmployeeProductivityData] = useState({
    employees: []
  })

  /** 依時間範圍與選擇的基準日期計算 startDate / endDate；後端需依此區間篩選資料 */
  const updateDateRange = useCallback(
    (range, base = selectedBaseDate) => {
      const today = tzNow()
      let start, end

      switch (range) {
        case 'daily':
          start = (base || today).startOf('day')
          end = (base || today).endOf('day')
          break
        case 'weekly':
          start = (base || today).startOf('isoWeek')
          end = (base || today).endOf('isoWeek')
          break
        case 'monthly':
          start = (base || today).startOf('month')
          end = (base || today).endOf('month')
          break
        case 'quarterly':
          start = (base || today).startOf('quarter')
          end = (base || today).endOf('quarter')
          break
        case 'yearly':
          start = (base || today).startOf('year')
          end = (base || today).endOf('year')
          break
        default:
          start = (base || today).startOf('day')
          end = (base || today).endOf('day')
      }

      setStartDate(start)
      setEndDate(end)
    },
    [selectedBaseDate]
  )

  const getTimeGranularity = (range) => {
    switch (range) {
      case 'daily':
        return 'day'
      case 'weekly':
        return 'week'
      case 'monthly':
        return 'month'
      case 'quarterly':
        return 'quarter'
      case 'yearly':
        return 'year'
      default:
        return 'day'
    }
  }

  const fetchRevenueData = useCallback(async () => {
    try {
      const response = await api.get(`${API_BASE_URL}/api/reports/revenue`, {
        params: {
          businessId,
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
          orderStatus: appointmentStatus,
          timeGranularity: getTimeGranularity(timeRange),
          scope
        }
      })
      const res = response.data || {}
      const co = res.checkedOut || {}
      const al = res.all || {}
      const sum = res.summary || {}
      const checkedOutData = co.data || []
      const checkedOutRevenue = co.totalRevenue ?? 0
      const checkedOutCount = co.totalCount ?? 0
      const checkedOutEmpty =
        checkedOutData.length === 0 &&
        checkedOutRevenue === 0 &&
        checkedOutCount === 0
      if (scope === 'checkedOut' && checkedOutEmpty) {
        setScope('all')
      }
      setRevenueData({
        scope: res.scope ?? scope,
        checkedOut: {
          data: checkedOutData,
          totalRevenue: checkedOutRevenue,
          totalCount: checkedOutCount,
          averageAmount: co.averageAmount ?? 0
        },
        all: {
          data: al.data || [],
          totalRevenue: al.totalRevenue ?? 0,
          totalCount: al.totalCount ?? 0,
          averageAmount: al.averageAmount ?? 0
        },
        totalValueAdded: res.totalValueAdded ?? sum.storedValueReceived ?? 0,
        summary: {
          totalRevenueAll:
            sum.totalRevenueAll ?? al.totalRevenue ?? sum.totalRevenue ?? 0,
          totalRevenueCheckedOut:
            sum.totalRevenueCheckedOut ??
            co.totalRevenue ??
            sum.totalRevenue ??
            0,
          storedValueReceived: sum.storedValueReceived ?? 0,
          // storedValueUsed 是帳本口徑（含沒開結帳單的扣款）；
          // storedValueUsedFromCheckout 是結帳口徑，與 ExcludingProduct 同基準
          storedValueUsed: sum.storedValueUsed ?? 0,
          storedValueUsedFromCheckout: sum.storedValueUsedFromCheckout ?? null,
          storedValueUsedExcludingProduct:
            sum.storedValueUsedExcludingProduct != null
              ? Number(sum.storedValueUsedExcludingProduct)
              : undefined,
          totalRevenueWithStoredValue: sum.totalRevenueWithStoredValue ?? 0,
          technologyRevenue: sum.technologyRevenue ?? 0,
          productSalesRevenue: sum.productSalesRevenue ?? 0,
          classTicketRevenue: sum.classTicketRevenue ?? 0,
          // 員工自收拆帳（有設定才有）：營收已只算繳回店家的部分
          selfCollected: sum.selfCollected || null,
          servicesAndProductsSubtotal: sum.servicesAndProductsSubtotal ?? 0,
          discountOrOtherAmount: sum.discountOrOtherAmount ?? 0
        }
      })
    } catch (error) {
      console.error('獲取營收數據失敗:', error)
      setRevenueData({
        scope,
        checkedOut: {
          data: [],
          totalRevenue: 0,
          totalCount: 0,
          averageAmount: 0
        },
        all: { data: [], totalRevenue: 0, totalCount: 0, averageAmount: 0 },
        totalValueAdded: 0,
        summary: {
          totalRevenueAll: 0,
          totalRevenueCheckedOut: 0,
          storedValueReceived: 0,
          storedValueUsed: 0,
          storedValueUsedFromCheckout: null,
          storedValueUsedExcludingProduct: undefined,
          totalRevenueWithStoredValue: 0,
          technologyRevenue: 0,
          productSalesRevenue: 0,
          classTicketRevenue: 0,
          servicesAndProductsSubtotal: 0,
          discountOrOtherAmount: 0
        }
      })
    }
  }, [businessId, startDate, endDate, appointmentStatus, timeRange, scope])

  const fetchProductSalesData = useCallback(async () => {
    try {
      const response = await api.get(
        `${API_BASE_URL}/api/reports/product-sales`,
        {
          params: {
            businessId,
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
            orderStatus: appointmentStatus
          }
        }
      )
      setProductSalesData(response.data)
    } catch (error) {
      console.error('獲取預約項目數據失敗:', error)
      setProductSalesData({
        productSalesReport: [],
        totalSales: 0,
        totalOrders: 0
      })
    }
  }, [businessId, startDate, endDate, appointmentStatus])

  const fetchCustomerSourceData = useCallback(async () => {
    try {
      const response = await api.get(
        `${API_BASE_URL}/api/reports/customer-source`,
        {
          params: {
            businessId,
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD')
          }
        }
      )
      setCustomerSourceData(response.data)
    } catch (error) {
      console.error('獲取客戶來源數據失敗:', error)
    }
  }, [businessId, startDate, endDate])

  // 顧客總量：全店累計顧客數（沿用顧客列表 API 的 pagination.totalCustomers，不受時間篩選）
  const fetchTotalCustomers = useCallback(async () => {
    if (!businessId) return
    try {
      const response = await api.get(
        `${API_BASE_URL}/api/business_B/${businessId}/customers`,
        { params: { page: 1, limit: 1 } }
      )
      const total = response.data?.pagination?.totalCustomers
      if (typeof total === 'number') setTotalCustomers(total)
    } catch (error) {
      console.error('獲取顧客總量失敗:', error)
    }
  }, [businessId])

  useEffect(() => {
    fetchTotalCustomers()
  }, [fetchTotalCustomers])

  // 顧客詳情：沉睡顧客（90 天未回訪）與本月壽星（皆不受時間篩選影響）
  const [dormantCustomers, setDormantCustomers] = useState({
    total: 0,
    list: []
  })
  const [birthdayCustomers, setBirthdayCustomers] = useState({
    total: 0,
    list: []
  })

  const fetchCustomerDetailLists = useCallback(async () => {
    if (!businessId) return
    try {
      const [dormantRes, birthdayRes] = await Promise.all([
        api.get(`${API_BASE_URL}/api/business_B/${businessId}/customers`, {
          params: { page: 1, limit: 8, lastVisitBeforeDays: 90 }
        }),
        api.get(`${API_BASE_URL}/api/business_B/${businessId}/customers`, {
          params: { page: 1, limit: 8, birthdayMonth: tzNow().month() + 1 }
        })
      ])
      setDormantCustomers({
        total: dormantRes.data?.pagination?.totalCustomers ?? 0,
        list: dormantRes.data?.data ?? []
      })
      setBirthdayCustomers({
        total: birthdayRes.data?.pagination?.totalCustomers ?? 0,
        list: birthdayRes.data?.data ?? []
      })
    } catch (error) {
      console.error('獲取顧客詳情名單失敗:', error)
    }
  }, [businessId])

  useEffect(() => {
    fetchCustomerDetailLists()
  }, [fetchCustomerDetailLists])

  const fetchCancellationData = useCallback(async () => {
    try {
      const response = await api.get(
        `${API_BASE_URL}/api/reports/customer-cancellation`,
        {
          params: {
            businessId,
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD')
          }
        }
      )
      setCancellationData(response.data)
    } catch (error) {
      console.error('獲取取消預訂數據失敗:', error)
      setCancellationData({
        totalBookings: 0,
        cancelledBookings: 0,
        cancellationRate: 0,
        cancelledCustomersCount: 0,
        cancelledCustomers: []
      })
    }
  }, [businessId, startDate, endDate])


  const fetchEmployeeProductivityData = useCallback(async () => {
    try {
      const response = await api.get(
        `${API_BASE_URL}/api/reports/${businessId}/employee-productivity`,
        {
          params: {
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD')
          }
        }
      )
      const data = response.data || {}
      setEmployeeProductivityData({
        employees: data.employees || []
      })
    } catch (error) {
      console.error('獲取員工產值數據失敗:', error)
      setEmployeeProductivityData({ employees: [] })
    }
  }, [businessId, startDate, endDate])

  const fetchReviewData = useCallback(async () => {
    try {
      const response = await api.get(
        `${API_BASE_URL}/api/reports/customer-review`,
        {
          params: {
            businessId,
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD')
          }
        }
      )
      setReviewData(response.data)
    } catch (error) {
      console.error('獲取客戶評價數據失敗:', error)
      setReviewData({
        totalReviews: 0,
        positiveReviews: 0,
        negativeReviews: 0,
        positiveRate: 0,
        negativeRate: 0
      })
    }
  }, [businessId, startDate, endDate])

  useEffect(() => {
    updateDateRange(timeRange)
  }, [timeRange, selectedBaseDate, updateDateRange])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      fetchRevenueData(),
      fetchProductSalesData(),
      fetchCustomerSourceData(),
      fetchCancellationData(),
      fetchReviewData(),
      fetchEmployeeProductivityData()
    ]).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [
    timeRange,
    startDate,
    endDate,
    fetchRevenueData,
    fetchProductSalesData,
    fetchCustomerSourceData,
    fetchCancellationData,
    fetchReviewData,
    fetchEmployeeProductivityData
  ])

  const formatXAxisLabel = useCallback(
    (date) => {
      const dateObj = dayjs(date)
      switch (timeRange) {
        case 'daily':
        case 'weekly':
        case 'monthly':
          return dateObj.format('MM-DD')
        case 'quarterly':
          return dateObj.format('YYYY-MM')
        case 'yearly':
          return dateObj.format('YYYY-MM')
        default:
          return date
      }
    },
    [timeRange]
  )

  const chartOption = useMemo(() => {
    let xAxisData = []
    let revenueSeriesData = []
    let countSeriesData = []

    const chartData =
      revenueData.scope === 'checkedOut'
        ? revenueData.checkedOut?.data
        : revenueData.all?.data
    if (chartData && chartData.length > 0) {
      switch (timeRange) {
        case 'daily':
          xAxisData = [formatXAxisLabel(chartData[0].date)]
          revenueSeriesData = [chartData[0].revenue]
          countSeriesData = [chartData[0].count]
          break
        case 'weekly':
        case 'monthly':
          if (chartData[0] && chartData[0].days) {
            xAxisData = chartData[0].days.map((day) =>
              formatXAxisLabel(day.date)
            )
            revenueSeriesData = chartData[0].days.map((day) => day.revenue)
            countSeriesData = chartData[0].days.map((day) => day.count)
          }
          break
        case 'quarterly':
        case 'yearly':
          xAxisData = chartData.map((item) => formatXAxisLabel(item.month))
          revenueSeriesData = chartData.map((item) => item.totalRevenue)
          countSeriesData = chartData.map((item) => item.totalCount)
          break
      }
    }

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        backgroundColor: colors.background.primary,
        borderColor: colors.border,
        textStyle: {
          color: colors.text.primary
        }
      },
      legend: {
        data: ['營收', '預約數'],
        bottom: 0,
        textStyle: {
          color: colors.text.secondary
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLine: {
          lineStyle: {
            color: colors.border
          }
        },
        axisLabel: {
          color: colors.text.secondary
        }
      },
      yAxis: [
        {
          type: 'value',
          name: '營收',
          axisLabel: {
            color: colors.text.secondary
          },
          axisLine: {
            lineStyle: {
              color: colors.border
            }
          },
          splitLine: {
            lineStyle: {
              color: colors.border,
              type: 'dashed'
            }
          }
        },
        {
          type: 'value',
          name: '預約數',
          axisLabel: {
            color: colors.text.secondary
          },
          axisLine: {
            lineStyle: {
              color: colors.border
            }
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: [
        {
          name: '營收',
          data: revenueSeriesData,
          type: 'bar',
          barWidth: '60%',
          itemStyle: {
            color: colors.secondary,
            borderRadius: [4, 4, 0, 0]
          }
        },
        {
          name: '預約數',
          data: countSeriesData,
          type: 'line',
          smooth: true,
          yAxisIndex: 1,
          itemStyle: {
            color: colors.accent
          },
          lineStyle: {
            width: 2
          }
        }
      ]
    }
  }, [revenueData, timeRange, formatXAxisLabel])

  const customerTypeChartOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)',
      backgroundColor: colors.background.primary,
      borderColor: colors.border,
      textStyle: {
        color: colors.text.primary
      }
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      data: ['新客戶', '回頭客'],
      textStyle: {
        color: colors.text.secondary
      }
    },
    series: [
      {
        name: '客戶類型',
        type: 'pie',
        radius: ['50%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 4,
          borderColor: colors.background.primary,
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: '16',
            fontWeight: '500',
            color: colors.text.primary
          }
        },
        labelLine: {
          show: false
        },
        data: [
          {
            value: customerSourceData.returningCustomers?.count ?? 0,
            name: '回頭客',
            itemStyle: {
              color: colors.secondary
            }
          },
          {
            value: customerSourceData.newCustomers?.count ?? 0,
            name: '新客戶',
            itemStyle: {
              color: colors.accent
            }
          }
        ]
      }
    ]
  }

  const customerSourceChartOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      backgroundColor: colors.background.primary,
      borderColor: colors.border,
      textStyle: {
        color: colors.text.primary
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: Object.keys(customerSourceData.customerSources ?? {}).map(
        (source) => (source === 'user' ? '客人線上預約' : '店家新增')
      ),
      axisLabel: {
        color: colors.text.secondary
      },
      axisLine: {
        lineStyle: {
          color: colors.border
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: colors.text.secondary
      },
      axisLine: {
        lineStyle: {
          color: colors.border
        }
      },
      splitLine: {
        lineStyle: {
          color: colors.border,
          type: 'dashed'
        }
      }
    },
    series: [
      {
        data: Object.keys(customerSourceData.customerSources ?? {}).map(
          (source) => ({
            value: (customerSourceData.customerSources ?? {})[source],
            itemStyle: {
              color: source === 'user' ? colors.secondary : colors.accent,
              borderRadius: [4, 4, 0, 0]
            }
          })
        ),
        type: 'bar'
      }
    ]
  }

  const ageDistributionChartOption = {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)',
      backgroundColor: colors.background.primary,
      borderColor: colors.border,
      textStyle: {
        color: colors.text.primary
      }
    },
    legend: {
      bottom: 0,
      left: 'center',
      textStyle: {
        color: colors.text.secondary
      }
    },
    grid: {
      left: '5%',
      right: '5%',
      top: '5%',
      bottom: '15%',
      containLabel: true
    },
    series: [
      {
        name: '年齡分佈',
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 4,
          borderColor: colors.background.primary,
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: '16',
            fontWeight: '500',
            color: colors.text.primary
          }
        },
        labelLine: {
          show: false
        },
        data: Object.entries(customerSourceData.ageDistribution ?? {}).map(
          ([key, value], index) => ({
            name: key,
            value: value,
            itemStyle: {
              color: [colors.secondary, colors.accent, colors.primary][
                index % 3
              ]
            }
          })
        )
      }
    ]
  }

  if (plan === 'free') {
    return (
      <PageContainer>
        <RestrictedAccess>
          <RestrictedIcon>
            <BarChartOutlined />
          </RestrictedIcon>
          <RestrictedTitle>數據分析功能限制</RestrictedTitle>
          <RestrictedDescription>
            親愛的用戶，數據分析功能僅開放給商務會員及以上等級的用戶使用。
            升級至商務會員方案，即可享有完整的數據分析功能，幫助您更好地了解業務狀況，
            做出更明智的經營決策。
          </RestrictedDescription>
          <UpgradeButton>升級方案</UpgradeButton>
        </RestrictedAccess>
      </PageContainer>
    )
  }

  // 依 scope 取用：總營收用 summary；總預約數／平均金額用 all 或 checkedOut
  const revenuePayload =
    revenueData.scope === 'checkedOut'
      ? revenueData.checkedOut
      : revenueData.all
  const displayTotalRevenue =
    revenueData.scope === 'checkedOut'
      ? (revenueData.summary?.totalRevenueCheckedOut ??
        revenuePayload?.totalRevenue ??
        0)
      : (revenueData.summary?.totalRevenueAll ??
        revenuePayload?.totalRevenue ??
        0)
  const averageRevenue =
    revenuePayload?.averageAmount ??
    ((revenuePayload?.totalCount ?? 0) > 0
      ? Math.round(
          (revenuePayload?.totalRevenue ?? 0) /
            (revenuePayload?.totalCount ?? 1)
        )
      : 0)

  // 權限檢查：需要 report:view 權限（數據報表已從 finance:view 拆出，獨立管控）
  if (!canViewReport) {
    return (
      <PageContainer>
        <Result
          status='403'
          title='權限不足'
          subTitle='您沒有權限查看數據報表。請聯繫老闆，到「員工管理 → 該員工 → 管理許可權」勾選「查看數據報表」權限。'
          extra={
            <Button type='primary' onClick={() => navigate('/')}>
              返回首頁
            </Button>
          }
        />
      </PageContainer>
    )
  }

  const renderAgeDistributionCard = () => (
    <ChartCard>
      <ChartHeader>
        <ChartTitle>
          <UserOutlined style={{ color: colors.secondary }} />
          <ChartTitleText>年齡分佈</ChartTitleText>
        </ChartTitle>
        <ChartSubtext>
          {startDate.format('YYYY-MM-DD')} 至 {endDate.format('YYYY-MM-DD')}
        </ChartSubtext>
      </ChartHeader>
      <ChartContainer>
        <ReactECharts
          option={ageDistributionChartOption}
          style={{ height: '100%', width: '100%', maxWidth: '100%' }}
          opts={{ renderer: 'svg', width: 'auto', height: 'auto' }}
        />
      </ChartContainer>
    </ChartCard>
  )

  return (
    <PageContainer>
      <DashboardHeader>
        <PageTitle>數據報表</PageTitle>
        <FiltersContainer>
          <FilterGroup>
            <FilterLabel>時間範圍</FilterLabel>
            <StyledSelect
              value={timeRange}
              onChange={(value) => {
                setTimeRange(value)
                updateDateRange(value)
              }}
            >
              <Select.Option value='daily'>今日</Select.Option>
              <Select.Option value='weekly'>本週</Select.Option>
              <Select.Option value='monthly'>本月</Select.Option>
              <Select.Option value='quarterly'>季度</Select.Option>
              <Select.Option value='yearly'>年</Select.Option>
            </StyledSelect>
            <Tooltip title='先選時間範圍，再於右側選擇要查詢的月份或年份（今日/本週可選具體日期）。後端須依 startDate、endDate 回傳該區間資料。'>
              <InfoCircleOutlined
                style={{ color: colors.text.tertiary, cursor: 'help' }}
              />
            </Tooltip>
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>查詢範圍</FilterLabel>
            <StyledSelect
              value={scope}
              onChange={(value) => setScope(value)}
              style={{ minWidth: 190 }}
              popupMatchSelectWidth={false}
            >
              <Select.Option value='all'>全部（已結帳＋未結帳）</Select.Option>
              <Select.Option value='checkedOut'>僅已結帳</Select.Option>
            </StyledSelect>
            <Tooltip title='全部：已結帳＋未結帳預約；僅已結帳：只含已結帳單，技術服務／商品以實收按比例計算。'>
              <InfoCircleOutlined
                style={{ color: colors.text.tertiary, cursor: 'help' }}
              />
            </Tooltip>
          </FilterGroup>
          <FilterGroup>
            <FilterLabel>
              {(timeRange === 'monthly' || timeRange === 'quarterly') &&
                '選擇月份'}
              {timeRange === 'yearly' && '選擇年份'}
              {(timeRange === 'daily' || timeRange === 'weekly') && '選擇日期'}
            </FilterLabel>
            {(timeRange === 'monthly' || timeRange === 'quarterly') && (
              <DatePicker
                picker='month'
                value={selectedBaseDate}
                onChange={(date) => {
                  if (date) {
                    setSelectedBaseDate(date)
                    const start = date.startOf('month')
                    const end =
                      timeRange === 'quarterly'
                        ? date.endOf('quarter')
                        : date.endOf('month')
                    setStartDate(start)
                    setEndDate(end)
                  }
                }}
                allowClear={false}
                style={{ minWidth: 140 }}
              />
            )}
            {timeRange === 'yearly' && (
              <DatePicker
                picker='year'
                value={selectedBaseDate}
                onChange={(date) => {
                  if (date) {
                    setSelectedBaseDate(date)
                    setStartDate(date.startOf('year'))
                    setEndDate(date.endOf('year'))
                  }
                }}
                allowClear={false}
                style={{ minWidth: 120 }}
              />
            )}
            {(timeRange === 'daily' || timeRange === 'weekly') && (
              <DatePicker
                value={selectedBaseDate}
                onChange={(date) => {
                  if (date) {
                    setSelectedBaseDate(date)
                    const start =
                      timeRange === 'daily'
                        ? date.startOf('day')
                        : date.startOf('isoWeek')
                    const end =
                      timeRange === 'daily'
                        ? date.endOf('day')
                        : date.endOf('isoWeek')
                    setStartDate(start)
                    setEndDate(end)
                  }
                }}
                allowClear={false}
                style={{ minWidth: 140 }}
              />
            )}
            <Tooltip title='可選擇要查詢的月份或年份；後端會依 startDate、endDate 篩選該區間內的報表資料。'>
              <InfoCircleOutlined
                style={{ color: colors.text.tertiary, cursor: 'help' }}
              />
            </Tooltip>
          </FilterGroup>
        </FiltersContainer>
      </DashboardHeader>

      <Spin spinning={loading} tip='載入中...' size='large'>
        <DashboardGrid>
          <MainContent>
            <Tabs
              activeKey={dashTab}
              onChange={setDashTab}
              items={[
                { key: 'overview', label: '總覽' },
                { key: 'customers', label: '顧客洞察' }
              ]}
              style={{ marginBottom: spacing.xs }}
            />
            {dashTab === 'overview' && (
            <>
            <StatsGrid>
              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <DollarOutlined />
                  </StatIcon>
                  <StatTitle>總營收</StatTitle>
                </StatHeader>
                <StatValue>
                  ${(displayTotalRevenue ?? 0).toLocaleString()}
                </StatValue>
                <StatSubtext>本期營收，包含技術服務營收和商品銷售</StatSubtext>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <CalendarOutlined />
                  </StatIcon>
                  <StatTitle>總預約數</StatTitle>
                </StatHeader>
                <StatValue>
                  {(revenuePayload?.totalCount ?? 0).toLocaleString()}
                </StatValue>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <BarChartOutlined />
                  </StatIcon>
                  <StatTitle>平均金額</StatTitle>
                </StatHeader>
                <StatValue>${Math.round(averageRevenue ?? 0).toLocaleString()}</StatValue>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <WalletOutlined />
                  </StatIcon>
                  <StatTitle>客戶儲值</StatTitle>
                </StatHeader>
                <StatValue>
                  $
                  {(
                    revenueData.summary?.storedValueReceived ??
                    revenueData.totalValueAdded ??
                    0
                  ).toLocaleString()}
                </StatValue>
                <StatSubtext>本期收到儲值金</StatSubtext>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <WalletOutlined />
                  </StatIcon>
                  <StatTitle>已使用儲值金</StatTitle>
                </StatHeader>
                <StatValue>
                  $
                  {(
                    revenueData.summary?.storedValueUsedFromCheckout ??
                    revenueData.summary?.storedValueUsed ??
                    0
                  ).toLocaleString()}
                </StatValue>
                <StatSubtext>
                  本期結帳單上用儲值支付(含商品銷售)
                </StatSubtext>
                {revenueData.summary?.storedValueUsedExcludingProduct != null && (
                  <StatSubtext style={{ marginTop: 2 }}>
                    不含商品銷售：NT$
                    {' '}
                    <span style={{ color: '#1a1a1a' }}>
                      {Number(
                        revenueData.summary.storedValueUsedExcludingProduct
                      ).toLocaleString()}
                    </span>
                  </StatSubtext>
                )}
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <DollarOutlined />
                  </StatIcon>
                  <StatTitle>技術服務營收</StatTitle>
                </StatHeader>
                <StatValue>
                  $
                  {Math.round(
                    revenueData.summary?.technologyRevenue ?? 0
                  ).toLocaleString()}
                </StatValue>
                {revenueData.summary?.selfCollected && (
                  <StatSubtext>
                    員工自收項目只計繳回店家 $
                    {Math.round(
                      revenueData.summary.selfCollected.storeShare || 0
                    ).toLocaleString()}
                  </StatSubtext>
                )}
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <ShoppingOutlined />
                  </StatIcon>
                  <StatTitle>商品銷售</StatTitle>
                </StatHeader>
                <StatValue>
                  $
                  {Math.round(
                    revenueData.summary?.productSalesRevenue ?? 0
                  ).toLocaleString()}
                </StatValue>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <CreditCardOutlined />
                  </StatIcon>
                  <StatTitle>堂票銷售</StatTitle>
                </StatHeader>
                <StatValue>
                  $
                  {Math.round(
                    revenueData.summary?.classTicketRevenue ?? 0
                  ).toLocaleString()}
                </StatValue>
                <StatSubtext>本期發放堂票售價加總</StatSubtext>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <CloseCircleOutlined />
                  </StatIcon>
                  <StatTitle>取消率</StatTitle>
                </StatHeader>
                <StatValue>
                  {((cancellationData.cancellationRate ?? 0) * 100).toFixed(1)}%
                </StatValue>
                <StatSubtext>
                  取消數:{' '}
                  {(cancellationData.cancelledBookings ?? 0).toLocaleString()}
                </StatSubtext>
              </StatCard>
            </StatsGrid>

            <ChartCard>
              <ChartHeader>
                <ChartTitle>
                  <DollarOutlined style={{ color: colors.secondary }} />
                  <ChartTitleText>營收拆解</ChartTitleText>
                </ChartTitle>
                <ChartSubtext>
                  {startDate.format('YYYY-MM-DD')} 至{' '}
                  {endDate.format('YYYY-MM-DD')}
                  {revenueData.scope === 'checkedOut'
                    ? ' · 僅已結帳'
                    : ' · 全部（已結帳＋未結帳）'}
                </ChartSubtext>
              </ChartHeader>
              <SummarySentence>
                技術服務 $
                {Math.round(
                  revenueData.summary?.technologyRevenue ?? 0
                ).toLocaleString()}
                {' ＋ 商品 $'}
                {Math.round(
                  revenueData.summary?.productSalesRevenue ?? 0
                ).toLocaleString()}
                {' ＝ 小計 $'}
                {(
                  revenueData.summary?.servicesAndProductsSubtotal ?? 0
                ).toLocaleString()}
                {'；總營收（折讓後）$'}
                {(displayTotalRevenue ?? 0).toLocaleString()}
                {'；差額 '}
                {(revenueData.summary?.discountOrOtherAmount ?? 0) < 0
                  ? '−'
                  : ''}
                $
                {Math.abs(
                  revenueData.summary?.discountOrOtherAmount ?? 0
                ).toLocaleString()}
                為整單折扣／課程／其他項目。
              </SummarySentence>
              <SummaryTable>
                <thead>
                  <tr>
                    <th>項目</th>
                    <th style={{ textAlign: 'right' }}>金額</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>技術服務營收</td>
                    <td>
                      $
                      {Math.round(
                        revenueData.summary?.technologyRevenue ?? 0
                      ).toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td>商品銷售</td>
                    <td>
                      $
                      {Math.round(
                        revenueData.summary?.productSalesRevenue ?? 0
                      ).toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td>小計（折扣前）</td>
                    <td>
                      $
                      {(
                        revenueData.summary?.servicesAndProductsSubtotal ?? 0
                      ).toLocaleString()}
                    </td>
                  </tr>
                  <tr>
                    <td>總營收（折讓後）</td>
                    <td>${(displayTotalRevenue ?? 0).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td>差額（折扣／課程／其他）</td>
                    <td
                      className={
                        (revenueData.summary?.discountOrOtherAmount ?? 0) < 0
                          ? 'amount-negative'
                          : ''
                      }
                    >
                      {(revenueData.summary?.discountOrOtherAmount ?? 0) >= 0
                        ? '$'
                        : '−$'}
                      {Math.abs(
                        revenueData.summary?.discountOrOtherAmount ?? 0
                      ).toLocaleString()}
                    </td>
                  </tr>
                </tbody>
              </SummaryTable>
            </ChartCard>

            <ChartCard>
              <ChartHeader>
                <ChartTitle>
                  <BarChartOutlined style={{ color: colors.secondary }} />
                  <ChartTitleText>營收和預約趨勢</ChartTitleText>
                </ChartTitle>
                <ChartSubtext>
                  {startDate.format('YYYY-MM-DD')} 至{' '}
                  {endDate.format('YYYY-MM-DD')}
                </ChartSubtext>
              </ChartHeader>
              <ChartContainer>
                <ReactECharts
                  option={chartOption}
                  style={{ height: '100%', width: '100%', maxWidth: '100%' }}
                  opts={{ renderer: 'svg', width: 'auto', height: 'auto' }}
                />
              </ChartContainer>
            </ChartCard>

            <ChartCard>
              <ChartHeader>
                <ChartTitle>
                  <ShoppingOutlined style={{ color: colors.secondary }} />
                  <ChartTitleText>預約項目熱門度分析</ChartTitleText>
                </ChartTitle>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.xs,
                    flexWrap: 'wrap'
                  }}
                >
                  <Segmented
                    size='small'
                    value={productSortBy}
                    onChange={setProductSortBy}
                    options={[
                      { label: '按消費金額', value: 'sales' },
                      { label: '按預約次數', value: 'count' }
                    ]}
                  />
                  <ChartSubtext>
                    {startDate.format('YYYY-MM-DD')} 至{' '}
                    {endDate.format('YYYY-MM-DD')}
                  </ChartSubtext>
                </div>
              </ChartHeader>
              <RankingScrollWrapper>
                {productSalesData.productSalesReport?.length > 0 ? (
                  <RankingList>
                    {[...productSalesData.productSalesReport]
                      .sort((a, b) =>
                        productSortBy === 'count'
                          ? (b.count ?? 0) - (a.count ?? 0) ||
                            (b.totalSales ?? 0) - (a.totalSales ?? 0)
                          : (b.totalSales ?? 0) - (a.totalSales ?? 0) ||
                            (b.count ?? 0) - (a.count ?? 0)
                      )
                      .map((product, idx) => (
                        <RankingItem key={product.id ?? product.name ?? idx}>
                          <span className='rank'>#{idx + 1}</span>
                          <span className='name' title={product.name}>
                            {product.name}
                          </span>
                          <span
                            className='amount'
                            style={
                              productSortBy === 'sales'
                                ? { fontWeight: 700 }
                                : undefined
                            }
                          >
                            $
                            {Math.round(
                              product.totalSales ?? 0
                            ).toLocaleString()}
                          </span>
                          <span
                            className='count'
                            style={
                              productSortBy === 'count'
                                ? {
                                    fontWeight: 700,
                                    color: colors.text.primary
                                  }
                                : undefined
                            }
                          >
                            {(product.count ?? 0).toLocaleString()} 次
                          </span>
                        </RankingItem>
                      ))}
                  </RankingList>
                ) : (
                  <span
                    style={{
                      color: colors.text.tertiary,
                      fontSize: 14,
                      padding: spacing.lg
                    }}
                  >
                    此區間尚無預約項目資料
                  </span>
                )}
              </RankingScrollWrapper>
            </ChartCard>

            <ChartCard>
              <ChartHeader>
                <ChartTitle>
                  <TeamOutlined style={{ color: colors.secondary }} />
                  <ChartTitleText>員工產值排行</ChartTitleText>
                </ChartTitle>
                <ChartSubtext>
                  {startDate.format('YYYY-MM-DD')} 至 {endDate.format('YYYY-MM-DD')}
                </ChartSubtext>
              </ChartHeader>
              <RankingScrollWrapper>
                {employeeProductivityData.employees?.length > 0 ? (
                  <RankingList>
                    {employeeProductivityData.employees.map((emp, idx) => (
                      <RankingItem key={emp.employeeId ?? idx}>
                        <span className='rank'>#{idx + 1}</span>
                        <span className='name' title={emp.employeeName}>
                          {emp.employeeName}
                        </span>
                        <span className='amount'>
                          $
                          {Math.round(emp.revenue ?? 0).toLocaleString()}
                        </span>
                        <span className='count'>
                          {(emp.serviceCount ?? 0).toLocaleString()} 次 · 客單 $
                          {Math.round(emp.averageOrderValue ?? 0).toLocaleString()} · 回訪{' '}
                          {((emp.returnRate ?? 0) * 100).toFixed(0)}%
                        </span>
                      </RankingItem>
                    ))}
                  </RankingList>
                ) : (
                  <span
                    style={{
                      color: colors.text.tertiary,
                      fontSize: 14,
                      padding: spacing.lg
                    }}
                  >
                    此區間尚無員工產值資料
                  </span>
                )}
              </RankingScrollWrapper>
            </ChartCard>
            </>
            )}

            {dashTab === 'customers' && (
            <>
            <StatsGrid>
              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <TeamOutlined />
                  </StatIcon>
                  <StatTitle>顧客總量</StatTitle>
                </StatHeader>
                <StatValue>
                  {totalCustomers != null
                    ? totalCustomers.toLocaleString()
                    : '—'}
                </StatValue>
                <StatSubtext>全店累計顧客數（不受時間篩選影響）</StatSubtext>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <UserOutlined />
                  </StatIcon>
                  <StatTitle>新客戶</StatTitle>
                </StatHeader>
                <StatValue>
                  {(
                    customerSourceData.newCustomers?.count ?? 0
                  ).toLocaleString()}
                </StatValue>
                <StatSubtext>
                  回頭客:{' '}
                  {(
                    customerSourceData.returningCustomers?.count ?? 0
                  ).toLocaleString()}
                  {customerSourceData.newCustomers?.withTopup != null && (
                    <>
                      {' ｜ 新客儲值: '}
                      {customerSourceData.newCustomers.withTopup.toLocaleString()}
                    </>
                  )}
                </StatSubtext>
              </StatCard>

              <StatCard>
                <StatHeader>
                  <StatIcon>
                    <StarOutlined />
                  </StatIcon>
                  <StatTitle>好評率</StatTitle>
                </StatHeader>
                <StatValue>
                  {((reviewData.positiveRate ?? 0) * 100).toFixed(1)}%
                </StatValue>
                <StatSubtext>
                  總評價: {(reviewData.totalReviews ?? 0).toLocaleString()}
                </StatSubtext>
              </StatCard>
            </StatsGrid>

            <ChartCard>
              <ChartHeader>
                <ChartTitle>
                  <UserOutlined style={{ color: colors.secondary }} />
                  <ChartTitleText>客戶來源分析</ChartTitleText>
                </ChartTitle>
                <ChartSubtext>
                  {startDate.format('YYYY-MM-DD')} 至{' '}
                  {endDate.format('YYYY-MM-DD')}
                </ChartSubtext>
              </ChartHeader>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: spacing.lg,
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <ChartContainer style={{ height: '280px' }}>
                  <ReactECharts
                    option={customerTypeChartOption}
                    style={{ height: '100%', width: '100%', maxWidth: '100%' }}
                    opts={{ renderer: 'svg', width: 'auto', height: 'auto' }}
                  />
                </ChartContainer>
                <ChartContainer style={{ height: '280px' }}>
                  <ReactECharts
                    option={customerSourceChartOption}
                    style={{ height: '100%', width: '100%', maxWidth: '100%' }}
                    opts={{ renderer: 'svg', width: 'auto', height: 'auto' }}
                  />
                </ChartContainer>
              </div>
            </ChartCard>

            {renderAgeDistributionCard()}

            {/* 顧客詳情：沉睡顧客／本月壽星／本期新客與回頭客名單 */}
            <ChartCard>
              <ChartHeader>
                <ChartTitle>
                  <TeamOutlined style={{ color: colors.secondary }} />
                  <ChartTitleText>顧客詳情</ChartTitleText>
                </ChartTitle>
                <ChartSubtext>
                  沉睡顧客與壽星為全店名單；新客／回頭客依上方時間篩選
                </ChartSubtext>
              </ChartHeader>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: spacing.md,
                  width: '100%'
                }}
              >
                {/* 沉睡顧客 */}
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      marginBottom: spacing.xs,
                      color: colors.text.primary
                    }}
                  >
                    沉睡顧客（90 天未回訪）· 共{' '}
                    {dormantCustomers.total.toLocaleString()} 位
                  </div>
                  {dormantCustomers.list.length === 0 ? (
                    <Text type='secondary' style={{ fontSize: 12 }}>
                      沒有超過 90 天未回訪的顧客
                    </Text>
                  ) : (
                    dormantCustomers.list.map((c) => (
                      <div
                        key={c._id}
                        onClick={() => navigate(`/customers/${c._id}`)}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 8,
                          padding: '6px 8px',
                          borderBottom: `1px solid ${colors.border}`,
                          cursor: 'pointer',
                          fontSize: 13
                        }}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {c.name || c.userInfo?.name || c.phone || '未命名'}
                        </span>
                        <span
                          style={{
                            color: colors.text.tertiary,
                            flexShrink: 0
                          }}
                        >
                          {c.visitStats?.lastVisitDate
                            ? `最後來訪 ${dayjs(
                                c.visitStats.lastVisitDate
                              ).format('YYYY/MM/DD')}`
                            : '無來訪紀錄'}
                        </span>
                      </div>
                    ))
                  )}
                </div>

                {/* 本月壽星 */}
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      marginBottom: spacing.xs,
                      color: colors.text.primary
                    }}
                  >
                    本月壽星（{tzNow().month() + 1} 月）· 共{' '}
                    {birthdayCustomers.total.toLocaleString()} 位
                  </div>
                  {birthdayCustomers.list.length === 0 ? (
                    <Text type='secondary' style={{ fontSize: 12 }}>
                      本月沒有壽星
                    </Text>
                  ) : (
                    birthdayCustomers.list.map((c) => (
                      <div
                        key={c._id}
                        onClick={() => navigate(`/customers/${c._id}`)}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 8,
                          padding: '6px 8px',
                          borderBottom: `1px solid ${colors.border}`,
                          cursor: 'pointer',
                          fontSize: 13
                        }}
                      >
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {c.name || c.userInfo?.name || c.phone || '未命名'}
                        </span>
                        <span
                          style={{
                            color: colors.text.tertiary,
                            flexShrink: 0
                          }}
                        >
                          {(c.userInfo?.birthdate || c.birthdate)
                            ? `生日 ${dayjs(
                                c.userInfo?.birthdate || c.birthdate
                              ).format('MM/DD')}`
                            : ''}
                        </span>
                      </div>
                    ))
                  )}
                </div>

                {/* 本期新客／回頭客名單 */}
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      marginBottom: spacing.xs,
                      color: colors.text.primary
                    }}
                  >
                    本期新客 ·{' '}
                    {(customerSourceData.newCustomers?.count ?? 0).toLocaleString()}{' '}
                    位
                  </div>
                  {(customerSourceData.newCustomers?.members ??
                    customerSourceData.newCustomers?.identifiers ??
                    [])
                    .slice(0, 8)
                    .map((m, idx) => {
                      const isObj = typeof m === 'object' && m !== null
                      const label = isObj
                        ? m.name || m.phone || m.identifier
                        : m
                      const cid = isObj ? m.id : null
                      return (
                        <div
                          key={cid || idx}
                          onClick={
                            cid ? () => navigate(`/customers/${cid}`) : undefined
                          }
                          style={{
                            padding: '6px 8px',
                            borderBottom: `1px solid ${colors.border}`,
                            cursor: cid ? 'pointer' : 'default',
                            fontSize: 13,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {label}
                        </div>
                      )
                    })}
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      margin: `${spacing.sm} 0 ${spacing.xs}`,
                      color: colors.text.primary
                    }}
                  >
                    本期回頭客 ·{' '}
                    {(
                      customerSourceData.returningCustomers?.count ?? 0
                    ).toLocaleString()}{' '}
                    位
                  </div>
                  {(customerSourceData.returningCustomers?.members ??
                    customerSourceData.returningCustomers?.identifiers ??
                    [])
                    .slice(0, 8)
                    .map((m, idx) => {
                      const isObj = typeof m === 'object' && m !== null
                      const label = isObj
                        ? m.name || m.phone || m.identifier
                        : m
                      const cid = isObj ? m.id : null
                      return (
                        <div
                          key={cid || idx}
                          onClick={
                            cid ? () => navigate(`/customers/${cid}`) : undefined
                          }
                          style={{
                            padding: '6px 8px',
                            borderBottom: `1px solid ${colors.border}`,
                            cursor: cid ? 'pointer' : 'default',
                            fontSize: 13,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {label}
                        </div>
                      )
                    })}
                </div>
              </div>
            </ChartCard>
            </>
            )}
          </MainContent>
        </DashboardGrid>
      </Spin>
    </PageContainer>
  )
}

export default DashboardPage
